import React, { useEffect, useState } from 'react';
import { PlusIcon, SearchIcon, EditIcon, EyeIcon, PhoneIcon, MailIcon, MapPinIcon, FileTextIcon, CalendarIcon, DownloadIcon, UsersIcon, ShieldIcon, ArchiveIcon, PowerOffIcon, CheckCircleIcon, RotateCcwIcon, XCircleIcon, ClipboardListIcon, ChevronDownIcon, ChevronUpIcon, StethoscopeIcon, ActivityIcon, PillIcon } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Modal } from '../ui/Modal';
import { Badge, getStatusBadgeVariant } from '../ui/Badge';
import { Tabs, TabPanel } from '../ui/Tabs';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { Patient, Gender } from '../../types';
import { formatDate, formatTime, getGenderName, getStatusName, getDoctorPrefix } from '../../utils/helpers';
import { validatePatientForm } from '../../utils/validation';
import { exportPatientsToExcel } from '../../utils/excelExport';
import { exportPatientsToPdf } from '../../utils/pdfExport';
import { usePagination } from '../../hooks/usePagination';
import { useSortableData } from '../../hooks/useSortableData';
import { Pagination } from '../ui/Pagination';
import { SortableHeader } from '../ui/SortableHeader';
import { ExcelExportModal } from '../ui/ExcelExportModal';
import { PdfExportModal } from '../ui/PdfExportModal';
export function Patients() {
  const {
    patients,
    addPatient,
    updatePatient,
    deletePatient,
    getPatient,
    insurances,
    getInsurance,
    getAppointmentsByPatient,
    getDoctor,
    getService,
    getSpecialty,
    doctors,
    appointments,
    getClinicalHistoriesByPatient
  } = useData();
  const {
    canCreate,
    canEdit,
    canDelete,
    isReadOnly,
    canView
  } = usePermissions();
  const readOnly = isReadOnly('patients');
  const {
    user
  } = useAuth();
  const isAdmin = user?.role === 'administrador';
  const userRoleNorm = user?.role?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') || '';
  const canExportExcel = isAdmin || user?.role === 'terapeuta' || user?.role === 'supervisor/a' || userRoleNorm === 'recepcion' || userRoleNorm === 'recepcionista';
  const canExportPdf = isAdmin || user?.role === 'terapeuta' || user?.role === 'supervisor/a' || userRoleNorm === 'recepcion' || userRoleNorm === 'recepcionista';
  // Check if user can see insurance field
  const canSeeInsurance = canView('patients_insurance');
  // Find the doctor record that matches the current user (by email)
  const currentUserDoctor = doctors.find(d => d.email?.toLowerCase() === user?.email?.toLowerCase());
  const isDoctorUser = !!currentUserDoctor && !isAdmin;
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [activeTab, setActiveTab] = useState('info');
  const [isActionConfirmOpen, setIsActionConfirmOpen] = useState(false);
  const [actionType, setActionType] = useState<'archive' | 'deactivate' | 'delete' | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isPdfExporting, setIsPdfExporting] = useState(false);
  const [isPdfExportModalOpen, setIsPdfExportModalOpen] = useState(false);
  // Expanded clinical history entries
  const [expandedVisitId, setExpandedVisitId] = useState<string | null>(null);
  // Clinical history statuses (read from same localStorage as ClinicalHistory module)
  const CH_STATUS_KEY = 'savidna-clinical-history-status';
  const [chStatuses, setChStatuses] = useState<Record<string, 'archived' | 'deactivated'>>({});
  useEffect(() => {
    try {
      const saved = localStorage.getItem(CH_STATUS_KEY);
      if (saved) setChStatuses(JSON.parse(saved));
    } catch {}
  }, []);
  // Listen for storage changes (in case ClinicalHistory module updates statuses)
  useEffect(() => {
    const handleStorage = () => {
      try {
        const saved = localStorage.getItem(CH_STATUS_KEY);
        if (saved) setChStatuses(JSON.parse(saved));
      } catch {}
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);
  const getClinicalHistoryStatus = (id: string): 'active' | 'archived' | 'deactivated' => chStatuses[id] || 'active';
  // Archive/Deactivate Logic
  const STORAGE_KEY = 'savidna-patients-status';
  const [itemStatuses, setItemStatuses] = useState<Record<string, 'archived' | 'deactivated'>>({});
  const [viewMode, setViewMode] = useState<'active' | 'archived' | 'deactivated'>('active');
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) setItemStatuses(JSON.parse(saved));
    } catch {}
  }, []);
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(itemStatuses));
  }, [itemStatuses]);
  const getItemStatus = (id: string): 'active' | 'archived' | 'deactivated' => itemStatuses[id] || 'active';
  const archiveItem = (id: string) => {
    setItemStatuses(prev => ({
      ...prev,
      [id]: 'archived'
    }));
    // Persist to Supabase
    updatePatient(id, {
      status: 'archived'
    } as any).catch(e => console.error('Error archiving patient in Supabase:', e));
    setIsActionConfirmOpen(false);
    setIsViewOpen(false);
  };
  const deactivateItem = (id: string) => {
    setItemStatuses(prev => ({
      ...prev,
      [id]: 'deactivated'
    }));
    // Persist to Supabase
    updatePatient(id, {
      status: 'deactivated'
    } as any).catch(e => console.error('Error deactivating patient in Supabase:', e));
    setIsActionConfirmOpen(false);
    setIsViewOpen(false);
  };
  const restoreItem = (id: string) => {
    setItemStatuses(prev => {
      const next = {
        ...prev
      };
      delete next[id];
      return next;
    });
    // Persist to Supabase
    updatePatient(id, {
      status: 'active'
    } as any).catch(e => console.error('Error restoring patient in Supabase:', e));
    setIsActionConfirmOpen(false);
    setIsViewOpen(false);
  };
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    gender: 'masculino' as Gender,
    phone: '',
    email: '',
    address: '',
    emergencyContactName: '',
    emergencyContactRelationship: '',
    emergencyContactPhone: '',
    insuranceId: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  // Filter patients: doctors only see patients they have appointments with
  const accessiblePatients = isDoctorUser && currentUserDoctor ? patients.filter(p => appointments.some(a => a.patientId === p.id && a.doctorId === currentUserDoctor.id)) : patients;
  const filteredPatients = accessiblePatients.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase()) || p.email.toLowerCase().includes(searchTerm.toLowerCase()) || p.phone.includes(searchTerm) || p.cedula && p.cedula.includes(searchTerm);
    const status = getItemStatus(p.id);
    return matchesSearch && status === viewMode;
  });
  const {
    sortedItems: sortedPatients,
    requestSort,
    isSorted
  } = useSortableData(filteredPatients);
  const {
    paginatedItems: paginatedPatients,
    currentPage,
    totalPages,
    totalItems,
    startIndex,
    endIndex,
    goToPage,
    nextPage,
    prevPage
  } = usePagination(sortedPatients);
  // Counts for tabs
  const activeCount = accessiblePatients.filter(p => getItemStatus(p.id) === 'active').length;
  const archivedCount = accessiblePatients.filter(p => getItemStatus(p.id) === 'archived').length;
  const deactivatedCount = accessiblePatients.filter(p => getItemStatus(p.id) === 'deactivated').length;
  const genderOptions = [{
    value: 'masculino',
    label: 'Masculino'
  }, {
    value: 'femenino',
    label: 'Femenino'
  }, {
    value: 'otro',
    label: 'Otro'
  }];
  const insuranceOptions = [{
    value: '',
    label: 'Sin seguro'
  }, ...insurances.map(i => ({
    value: i.id,
    label: i.name
  }))];
  // Format Cédula as user types
  const handleCedulaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, ''); // Remove non-digits
    if (value.length > 11) value = value.slice(0, 11); // Limit to 11 digits
    // Format: 000-0000000-0
    let formatted = value;
    if (value.length > 3) {
      formatted = value.slice(0, 3) + '-' + value.slice(3);
    }
    if (value.length > 10) {
      formatted = formatted.slice(0, 11) + '-' + formatted.slice(11);
    }
    setFormData(prev => ({
      ...prev,
      cedula: formatted
    }));
  };
  const openNewForm = () => {
    if (!canCreate('patients')) return;
    setSelectedPatient(null);
    setFormData({
      name: '',
      gender: 'masculino',
      phone: '',
      email: '',
      address: '',
      emergencyContactName: '',
      emergencyContactRelationship: '',
      emergencyContactPhone: '',
      insuranceId: ''
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openEditForm = (patient: Patient) => {
    if (!canEdit('patients')) return;
    setSelectedPatient(patient);
    setFormData({
      name: patient.name,
      gender: patient.gender,
      phone: patient.phone,
      email: patient.email,
      address: patient.address,
      emergencyContactName: patient.emergencyContact?.name || '',
      emergencyContactRelationship: patient.emergencyContact?.relationship || '',
      emergencyContactPhone: patient.emergencyContact?.phone || '',
      insuranceId: patient.insuranceId || ''
    });
    setFormErrors({});
    setIsViewOpen(false);
    setIsFormOpen(true);
  };
  const openView = (patient: Patient) => {
    setSelectedPatient(patient);
    // Re-read clinical history statuses when opening view
    try {
      const saved = localStorage.getItem(CH_STATUS_KEY);
      if (saved) setChStatuses(JSON.parse(saved));
    } catch {}
    setActiveTab('info');
    setIsViewOpen(true);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validation = validatePatientForm(formData);
    if (!validation.isValid) {
      setFormErrors(validation.errors);
      return;
    }
    const patientData = {
      name: capitalizeName(formData.name),
      gender: formData.gender,
      phone: formData.phone,
      email: formData.email,
      address: formData.address,
      emergencyContact: {
        name: capitalizeName(formData.emergencyContactName),
        relationship: formData.emergencyContactRelationship,
        phone: formData.emergencyContactPhone
      },
      insuranceId: formData.insuranceId || null
    };
    try {
      if (selectedPatient) {
        await updatePatient(selectedPatient.id, patientData);
      } else {
        await addPatient(patientData);
      }
      setIsFormOpen(false);
    } catch (error: any) {
      console.error('Error guardando paciente:', error);
      setFormErrors({
        general: error?.message || 'Error al guardar el paciente'
      });
    }
  };
  const openActionConfirm = (patient: Patient, type: 'archive' | 'deactivate' | 'delete') => {
    setSelectedPatient(patient);
    setActionType(type);
    setIsActionConfirmOpen(true);
  };
  const handleAction = () => {
    if (!selectedPatient || !actionType) return;
    if (actionType === 'archive') {
      archiveItem(selectedPatient.id);
    } else if (actionType === 'deactivate') {
      deactivateItem(selectedPatient.id);
    } else if (actionType === 'delete') {
      deletePatient(selectedPatient.id);
      setIsActionConfirmOpen(false);
      setIsViewOpen(false);
    }
  };
  const saveMedicalHistory = async () => {
    if (readOnly) return;
    if (selectedPatient && medicalData) {
      setIsSavingMedical(true);
      setMedicalSaveError('');
      setMedicalSaveSuccess(false);
      try {
        await updateMedicalHistory(selectedPatient.id, medicalData);
        setMedicalSaveSuccess(true);
        setTimeout(() => setMedicalSaveSuccess(false), 3000);
      } catch (error: any) {
        console.error('❌ Error guardando historial médico:', error);
        setMedicalSaveError(error?.message || 'Error al guardar el historial médico');
      } finally {
        setIsSavingMedical(false);
      }
    }
  };
  const tabs = [{
    id: 'info',
    label: 'Información',
    icon: <FileTextIcon className="w-4 h-4" data-id="element-3296" />
  }, {
    id: 'visits',
    label: 'Registro de Visitas',
    icon: <ClipboardListIcon className="w-4 h-4" data-id="element-3297" />
  }, {
    id: 'appointments',
    label: 'Citas',
    icon: <CalendarIcon className="w-4 h-4" data-id="element-3298" />
  }];
  const handleExportToExcel = async (dateFrom: string | null, dateTo: string | null) => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    let dataToExport = patients;
    if (dateFrom && dateTo) {
      dataToExport = patients.filter(p => {
        const dateStr = new Date(p.createdAt).toISOString().split('T')[0];
        return dateStr >= dateFrom && dateStr <= dateTo;
      });
    }
    exportPatientsToExcel(dataToExport, (insuranceId: string | null) => {
      if (!insuranceId) return 'Sin seguro';
      const insurance = getInsurance(insuranceId);
      return insurance?.name || 'Sin seguro';
    });
    setIsExporting(false);
    setIsExportModalOpen(false);
  };
  const handleExportToPdf = async (dateFrom: string | null, dateTo: string | null) => {
    setIsPdfExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    let dataToExport = patients;
    if (dateFrom && dateTo) {
      dataToExport = patients.filter(p => {
        const dateStr = new Date(p.createdAt).toISOString().split('T')[0];
        return dateStr >= dateFrom && dateStr <= dateTo;
      });
    }
    exportPatientsToPdf(dataToExport, (insuranceId: string | null) => {
      if (!insuranceId) return 'Sin seguro';
      const insurance = getInsurance(insuranceId);
      return insurance?.name || 'Sin seguro';
    });
    setIsPdfExporting(false);
    setIsPdfExportModalOpen(false);
  };
  // Helper to capitalize each word in a name
  const capitalizeName = (name: string) => {
    return name.trim().split(/\s+/).map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
  };
  return <div className="space-y-6" data-id="element-3299">
      {/* Header Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-teal-600 via-teal-500 to-cyan-500 p-6 text-white shadow-xl" data-id="element-3300">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-3301" />
        <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4 w-full" data-id="element-3302">
          <div data-id="element-3303">
            <div className="flex items-center gap-3 mb-2" data-id="element-3304">
              <div className="p-2.5 bg-white/15 backdrop-blur-sm rounded-xl" data-id="element-3305">
                <UsersIcon className="w-6 h-6" data-id="element-3306" />
              </div>
              <h1 className="text-2xl font-bold" data-id="element-3307">Pacientes</h1>
            </div>
            <p className="text-teal-100 max-w-xl" data-id="element-3308">
              {isDoctorUser ? 'Mis pacientes — Solo se muestran pacientes con citas asignadas a usted' : 'Gestión de pacientes y expedientes médicos'}
              {readOnly && <span className="ml-2 bg-white/20 px-2 py-0.5 rounded text-xs font-medium" data-id="element-3309">
                  Solo lectura
                </span>}
            </p>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-3310">
            {canExportPdf && <Button variant="outline" leftIcon={<FileTextIcon className="w-4 h-4" data-id="element-3312" />} onClick={() => setIsPdfExportModalOpen(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-3311">
                Exportar PDF
              </Button>}
            {canExportExcel && <Button variant="outline" leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-3314" />} onClick={() => setIsExportModalOpen(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-3313">
                Exportar Excel
              </Button>}
            {canCreate('patients') && <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-3316" />} onClick={openNewForm} className="bg-white text-teal-700 hover:bg-teal-50 shadow-lg shadow-teal-900/20" data-id="element-3315">
                Nuevo Paciente
              </Button>}
          </div>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="flex flex-col md:flex-row gap-4 justify-between items-end" data-id="element-3317">
        <div className="w-full md:w-96 relative" data-id="element-3318">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-3319" />
          <Input placeholder="Buscar por nombre, cédula, correo o teléfono..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700" data-id="element-3320" />
        </div>

        {/* View Mode Tabs */}
        <div className="flex items-center gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl w-full md:w-auto overflow-x-auto" data-id="element-3321">
          {[{
          key: 'active',
          label: 'Activos',
          count: activeCount,
          icon: CheckCircleIcon
        }, {
          key: 'archived',
          label: 'Archivados',
          count: archivedCount,
          icon: ArchiveIcon
        }, {
          key: 'deactivated',
          label: 'Desactivados',
          count: deactivatedCount,
          icon: XCircleIcon
        }].map(tab => <button key={tab.key} onClick={() => setViewMode(tab.key as any)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${viewMode === tab.key ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} data-id="element-3322">
              <tab.icon className="w-4 h-4" data-id="element-3323" />
              {tab.label}
              <span className="text-xs bg-gray-200 dark:bg-gray-600 px-1.5 py-0.5 rounded-full" data-id="element-3324">
                {tab.count}
              </span>
            </button>)}
        </div>
      </div>

      {/* Patients List */}
      {/* ── MOBILE CARD LIST ── */}
      <div className="sm:hidden space-y-0 divide-y divide-gray-100 dark:divide-gray-800 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-lg overflow-hidden" data-id="element-3325">
        {paginatedPatients.length === 0 ? <div className="py-12 text-center text-gray-500 dark:text-gray-400" data-id="element-3326">
          <UsersIcon className="w-10 h-10 mx-auto mb-3 text-gray-300 dark:text-gray-600" data-id="element-3327" />
          <p className="text-sm" data-id="element-3328">{searchTerm ? 'No hay resultados' : 'No hay pacientes'}</p>
        </div> : paginatedPatients.map(patient => <div key={patient.id} className="px-4 py-3 flex items-center gap-3" data-id="element-3329">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0" data-id="element-3330">
              {patient.name.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0" data-id="element-3331">
              <p className="font-semibold text-gray-900 dark:text-white text-sm truncate capitalize" data-id="element-3332">{patient.name}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate" data-id="element-3333">{patient.phone} · {patient.email}</p>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0" data-id="element-3334">
              <button onClick={() => openView(patient)} className="p-2 text-gray-400 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded-lg transition-colors" title="Ver" data-id="element-3335"><EyeIcon className="w-4 h-4" data-id="element-3336" /></button>
              {viewMode === 'active' && canEdit('patients') && <button onClick={() => openEditForm(patient)} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors" title="Editar" data-id="element-3337"><EditIcon className="w-4 h-4" data-id="element-3338" /></button>}
            </div>
          </div>)}
        {/* Mobile pagination */}
        <div className="px-4 pb-3 pt-2 border-t border-gray-100 dark:border-gray-800" data-id="element-3339">
          <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} startIndex={startIndex} endIndex={endIndex} onPageChange={goToPage} onNext={nextPage} onPrev={prevPage} data-id="element-3340" />
        </div>
      </div>

      <div className="hidden sm:block" data-id="element-3341">
      <Card padding="none" className="overflow-hidden border-0 shadow-lg" data-id="element-3342">
        <div className="h-1 bg-gradient-to-r from-teal-500 to-cyan-500" data-id="element-3343" />
        <div className="overflow-x-auto" data-id="element-3344">
          <table className="w-full min-w-[600px]" data-id="element-3345">
            <thead data-id="element-3346">
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50" data-id="element-3347">
                <SortableHeader label="Paciente" sortKey="name" currentDirection={isSorted('name')} onSort={requestSort} data-id="element-3348" />
                <SortableHeader label="Contacto" sortKey="phone" currentDirection={isSorted('phone')} onSort={requestSort} data-id="element-3349" />
                <SortableHeader label="Dirección" sortKey="address" currentDirection={isSorted('address')} onSort={requestSort} className="hidden sm:table-cell" data-id="element-3350" />
                <th className="text-right py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-3351">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800" data-id="element-3352">
              {filteredPatients.length === 0 ? <tr data-id="element-3353">
                  <td colSpan={4} className="py-16 text-center text-gray-500 dark:text-gray-400" data-id="element-3354">
                    <div className="flex flex-col items-center justify-center" data-id="element-3355">
                      <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4" data-id="element-3356">
                        <UsersIcon className="w-8 h-8 text-gray-400" data-id="element-3357" />
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1" data-id="element-3358">
                        No se encontraron pacientes
                      </h3>
                      <p className="text-sm" data-id="element-3359">
                        {searchTerm ? 'Intenta con otros términos de búsqueda' : viewMode === 'active' ? 'Comienza creando un nuevo paciente' : 'No hay pacientes en esta vista'}
                      </p>
                    </div>
                  </td>
                </tr> : paginatedPatients.map(patient => {
                return <tr key={patient.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors group" data-id="element-3360">
                      <td className="py-4 px-6" data-id="element-3361">
                        <div className="flex items-center gap-3" data-id="element-3362">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center text-white font-bold shadow-sm" data-id="element-3363">
                            {patient.name.charAt(0).toUpperCase()}
                          </div>
                          <div data-id="element-3364">
                            <p className="font-medium text-gray-900 dark:text-white capitalize" data-id="element-3365">
                              {patient.name}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-3366">
                              {getGenderName(patient.gender)}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6" data-id="element-3367">
                        <div className="space-y-1" data-id="element-3368">
                          <p className="text-sm text-gray-900 dark:text-gray-100 flex items-center gap-1.5" data-id="element-3369">
                            <PhoneIcon className="w-3.5 h-3.5 text-gray-400" data-id="element-3370" />{' '}
                            {patient.phone}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1.5" data-id="element-3371">
                            <MailIcon className="w-3.5 h-3.5 text-gray-400" data-id="element-3372" />{' '}
                            {patient.email}
                          </p>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600 dark:text-gray-400 hidden sm:table-cell" data-id="element-3373">
                        {patient.address || '-'}
                      </td>
                      <td className="py-4 px-6" data-id="element-3374">
                        <div className="flex items-center justify-end gap-2 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity" data-id="element-3375">
                          <button onClick={() => openView(patient)} className="p-2 text-gray-500 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded-lg transition-colors" title="Ver detalles" data-id="element-3376">
                            <EyeIcon className="w-4 h-4" data-id="element-3377" />
                          </button>

                          {viewMode === 'active' ? <>
                              {canEdit('patients') && <button onClick={() => openEditForm(patient)} className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors" title="Editar" data-id="element-3378">
                                  <EditIcon className="w-4 h-4" data-id="element-3379" />
                                </button>}
                              {canDelete('patients') && <>
                                  <button onClick={() => openActionConfirm(patient, 'archive')} className="p-2 text-gray-500 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors" title="Archivar" data-id="element-3380">
                                    <ArchiveIcon className="w-4 h-4" data-id="element-3381" />
                                  </button>
                                  <button onClick={() => openActionConfirm(patient, 'deactivate')} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" title="Desactivar" data-id="element-3382">
                                    <PowerOffIcon className="w-4 h-4" data-id="element-3383" />
                                  </button>
                                </>}
                            </> : <button onClick={() => restoreItem(patient.id)} className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors" title="Restaurar" data-id="element-3384">
                              <RotateCcwIcon className="w-4 h-4" data-id="element-3385" />
                            </button>}
                        </div>
                      </td>
                    </tr>;
              })}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className="px-6 pb-4 border-t border-gray-100 dark:border-gray-700 pt-4" data-id="element-3386">
          <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} startIndex={startIndex} endIndex={endIndex} onPageChange={goToPage} onNext={nextPage} onPrev={prevPage} data-id="element-3387" />
        </div>
      </Card>

      {/* Patient Form Modal */}
      </div>

      <Modal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} title={selectedPatient ? 'Editar Paciente' : 'Nuevo Paciente'} size="lg" icon={<UsersIcon className="w-5 h-5" data-id="element-3389" />} accentColor="emerald" data-id="element-3388">
        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-3390">
          {/* General Error Message */}
          {formErrors.general && <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-sm text-red-600 dark:text-red-400 font-medium" data-id="element-3391">
              {formErrors.general}
            </div>}
          {/* Personal Info Section */}
          <div data-id="element-3392">
            <div className="flex items-center gap-2 mb-4" data-id="element-3393">
              <span className="w-1.5 h-5 rounded-full bg-emerald-500" data-id="element-3394"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-3395">
                Información Personal
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-3396">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3397">
                <Input label="Nombre Completo" value={formData.name} onChange={e => setFormData({
                ...formData,
                name: e.target.value
              })} error={formErrors.name} placeholder="Nombre del paciente" data-id="element-3398" />
                <Select label="Sexo" options={genderOptions} value={formData.gender} onChange={value => setFormData({
                ...formData,
                gender: value as Gender
              })} error={formErrors.gender} data-id="element-3399" />
              </div>

              <Input label="Dirección" value={formData.address} onChange={e => setFormData({
              ...formData,
              address: e.target.value
            })} placeholder="Dirección completa" data-id="element-3400" />
            </div>
          </div>

          {/* Contact Section */}
          <div data-id="element-3401">
            <div className="flex items-center gap-2 mb-4" data-id="element-3402">
              <span className="w-1.5 h-5 rounded-full bg-teal-500" data-id="element-3403"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-3404">
                Contacto
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-3405">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3406">
                <Input label="Teléfono" value={formData.phone} onChange={e => setFormData({
                ...formData,
                phone: e.target.value
              })} error={formErrors.phone} placeholder="809-000-0000" data-id="element-3407" />
                <Input type="email" label="Correo Electrónico" value={formData.email} onChange={e => setFormData({
                ...formData,
                email: e.target.value
              })} error={formErrors.email} placeholder="paciente@email.com" data-id="element-3408" />
              </div>
            </div>
          </div>

          {/* Emergency Contact Section */}
          <div data-id="element-3409">
            <div className="flex items-center gap-2 mb-4" data-id="element-3410">
              <span className="w-1.5 h-5 rounded-full bg-rose-500" data-id="element-3411"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-3412">
                Contacto de Emergencia
              </h3>
            </div>
            <div className="bg-red-50/50 dark:bg-red-900/5 rounded-xl p-4 space-y-4 border border-red-100 dark:border-red-900/20" data-id="element-3413">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3414">
                <Input label="Nombre Completo" value={formData.emergencyContactName} onChange={e => setFormData({
                ...formData,
                emergencyContactName: e.target.value
              })} placeholder="Nombre del contacto" data-id="element-3415" />
                <Input label="Parentesco" value={formData.emergencyContactRelationship} onChange={e => setFormData({
                ...formData,
                emergencyContactRelationship: e.target.value
              })} placeholder="Ej: Madre, Esposo" data-id="element-3416" />
              </div>
              <Input label="Teléfono de Emergencia" value={formData.emergencyContactPhone} onChange={e => setFormData({
              ...formData,
              emergencyContactPhone: e.target.value
            })} placeholder="Número de contacto" data-id="element-3417" />
            </div>
          </div>

          {/* Insurance Section - Only visible with patients_insurance permission */}
          {canSeeInsurance && <div data-id="element-3418">
              <div className="flex items-center gap-2 mb-4" data-id="element-3419">
                <span className="w-1.5 h-5 rounded-full bg-sky-500" data-id="element-3420"></span>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-3421">
                  Seguro Médico
                </h3>
              </div>
              <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border border-gray-100 dark:border-gray-700/50" data-id="element-3422">
                <Select label="Seguro Médico" options={insuranceOptions} value={formData.insuranceId} onChange={value => setFormData({
              ...formData,
              insuranceId: value
            })} data-id="element-3423" />
              </div>
            </div>}

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700" data-id="element-3424">
            <Button variant="outline" type="button" onClick={() => setIsFormOpen(false)} data-id="element-3425">
              Cancelar
            </Button>
            <Button type="submit" data-id="element-3426">
              {selectedPatient ? 'Guardar Cambios' : 'Crear Paciente'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Patient View Modal */}
      <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} title="Expediente del Paciente" size="xl" data-id="element-3427">
        {selectedPatient && <div className="space-y-6" data-id="element-3428">
            {/* Hero Header */}
            <div className="relative rounded-xl overflow-hidden bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm" data-id="element-3429">
              <div className="px-6 py-5" data-id="element-3430">
                <div className="flex items-center gap-4" data-id="element-3431">
                  <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-teal-900/50 dark:to-cyan-900/50 flex items-center justify-center text-3xl font-bold text-teal-600 dark:text-teal-400 shadow-sm border border-gray-100 dark:border-gray-700 flex-shrink-0" data-id="element-3432">
                    {selectedPatient.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-3433">
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white capitalize truncate" data-id="element-3434">
                      {selectedPatient.name}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 font-medium capitalize" data-id="element-3435">
                      {getGenderName(selectedPatient.gender)}
                    </p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0" data-id="element-3436">
                    {!readOnly && viewMode === 'active' && <>
                        {canEdit('patients') && <Button variant="outline" size="sm" onClick={() => openEditForm(selectedPatient)} data-id="element-3437">
                            <EditIcon className="w-4 h-4 mr-2" data-id="element-3438" /> Editar
                          </Button>}
                        {canDelete('patients') && <Button variant="danger" size="sm" onClick={() => openActionConfirm(selectedPatient, 'archive')} data-id="element-3439">
                            <ArchiveIcon className="w-4 h-4" data-id="element-3440" />
                          </Button>}
                      </>}
                  </div>
                </div>
              </div>
            </div>

            <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} data-id="element-3441">
              <TabPanel id="info" activeTab={activeTab} data-id="element-3442">
                <div className="space-y-6" data-id="element-3443">
                  {/* Contact Info Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3444">
                    <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700" data-id="element-3445">
                      <div className="flex items-center gap-3 mb-3" data-id="element-3446">
                        <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400" data-id="element-3447">
                          <PhoneIcon className="w-4 h-4" data-id="element-3448" />
                        </div>
                        <h4 className="font-semibold text-gray-900 dark:text-white" data-id="element-3449">
                          Teléfono
                        </h4>
                      </div>
                      <p className="text-gray-600 dark:text-gray-300 pl-11" data-id="element-3450">
                        {selectedPatient.phone}
                      </p>
                    </div>

                    <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700" data-id="element-3451">
                      <div className="flex items-center gap-3 mb-3" data-id="element-3452">
                        <div className="w-8 h-8 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 dark:text-purple-400" data-id="element-3453">
                          <MailIcon className="w-4 h-4" data-id="element-3454" />
                        </div>
                        <h4 className="font-semibold text-gray-900 dark:text-white" data-id="element-3455">
                          Correo
                        </h4>
                      </div>
                      <p className="text-gray-600 dark:text-gray-300 pl-11" data-id="element-3456">
                        {selectedPatient.email}
                      </p>
                    </div>

                    <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700 md:col-span-2" data-id="element-3457">
                      <div className="flex items-center gap-3 mb-3" data-id="element-3458">
                        <div className="w-8 h-8 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center text-orange-600 dark:text-orange-400" data-id="element-3459">
                          <MapPinIcon className="w-4 h-4" data-id="element-3460" />
                        </div>
                        <h4 className="font-semibold text-gray-900 dark:text-white" data-id="element-3461">
                          Dirección
                        </h4>
                      </div>
                      <p className="text-gray-600 dark:text-gray-300 pl-11" data-id="element-3462">
                        {selectedPatient.address}
                      </p>
                    </div>

                    {/* Insurance Card - Only visible with permission */}
                    {canSeeInsurance && <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700 md:col-span-2" data-id="element-3463">
                        <div className="flex items-center gap-3 mb-3" data-id="element-3464">
                          <div className="w-8 h-8 rounded-lg bg-sky-100 dark:bg-sky-900/30 flex items-center justify-center text-sky-600 dark:text-sky-400" data-id="element-3465">
                            <ShieldIcon className="w-4 h-4" data-id="element-3466" />
                          </div>
                          <h4 className="font-semibold text-gray-900 dark:text-white" data-id="element-3467">
                            Seguro Médico
                          </h4>
                        </div>
                        <p className="text-gray-600 dark:text-gray-300 pl-11" data-id="element-3468">
                          {selectedPatient.insuranceId ? getInsurance(selectedPatient.insuranceId)?.name : 'Sin seguro'}
                        </p>
                      </div>}
                  </div>

                  {/* Emergency Contact Section */}
                  <div className="rounded-xl border border-red-100 dark:border-red-900/30 bg-red-50/50 dark:bg-red-900/10 p-5" data-id="element-3469">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2" data-id="element-3470">
                      <span className="w-2 h-6 rounded-full bg-red-500" data-id="element-3471"></span>
                      Contacto de Emergencia
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6" data-id="element-3472">
                      <div data-id="element-3473">
                        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1" data-id="element-3474">
                          Nombre
                        </h4>
                        <p className="text-gray-900 dark:text-white font-medium" data-id="element-3475">
                          {selectedPatient.emergencyContact?.name || '-'}
                        </p>
                      </div>
                      <div data-id="element-3476">
                        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1" data-id="element-3477">
                          Parentesco
                        </h4>
                        <p className="text-gray-900 dark:text-white font-medium" data-id="element-3478">
                          {selectedPatient.emergencyContact?.relationship || '-'}
                        </p>
                      </div>
                      <div data-id="element-3479">
                        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1" data-id="element-3480">
                          Teléfono
                        </h4>
                        <p className="text-gray-900 dark:text-white font-medium flex items-center gap-2" data-id="element-3481">
                          <PhoneIcon className="w-4 h-4 text-red-500" data-id="element-3482" />
                          {selectedPatient.emergencyContact?.phone || '-'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </TabPanel>

              <TabPanel id="appointments" activeTab={activeTab} data-id="element-3483">
                <div className="space-y-4" data-id="element-3484">
                  {getAppointmentsByPatient(selectedPatient.id).length === 0 ? <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-dashed border-gray-200 dark:border-gray-700" data-id="element-3485">
                      <CalendarIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" data-id="element-3486" />
                      <p className="text-gray-500 dark:text-gray-400" data-id="element-3487">
                        No hay citas registradas
                      </p>
                    </div> : getAppointmentsByPatient(selectedPatient.id).map(apt => {
                const doctor = getDoctor(apt.doctorId);
                const service = getService(apt.serviceId);
                return <div key={apt.id} className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl hover:shadow-sm transition-shadow" data-id="element-3488">
                          <div className="flex items-center gap-4" data-id="element-3489">
                            <div className="w-10 h-10 rounded-full bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center text-emerald-600 dark:text-emerald-400 font-bold" data-id="element-3490">
                              {doctor?.name.charAt(0)}
                            </div>
                            <div data-id="element-3491">
                              <p className="font-medium text-gray-900 dark:text-white" data-id="element-3492">
                                {apt.date} - {formatTime(apt.time)}
                              </p>
                              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-3493">
                                {doctor ? `${getDoctorPrefix(doctor.gender, getSpecialty(doctor.specialtyId)?.name)} ${doctor.name}` : '-'}{' '}
                                • {service?.name}
                              </p>
                            </div>
                          </div>
                          <Badge variant={getStatusBadgeVariant(apt.status)} data-id="element-3494">
                            {getStatusName(apt.status)}
                          </Badge>
                        </div>;
              })}
                </div>
              </TabPanel>

              {/* Registro de Visitas Tab */}
              <TabPanel id="visits" activeTab={activeTab} data-id="element-3495">
                {(() => {
              const patientVisits = getClinicalHistoriesByPatient(selectedPatient.id).filter(v => getClinicalHistoryStatus(v.id) === 'active').sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
              return <div className="space-y-4" data-id="element-3496">
                      {/* Summary Stats */}
                      {patientVisits.length > 0 && <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-id="element-3497">
                          <div className="p-3 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800/30" data-id="element-3498">
                            <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-400" data-id="element-3499">
                              {patientVisits.length}
                            </p>
                            <p className="text-xs text-emerald-600 dark:text-emerald-500 font-medium" data-id="element-3500">
                              Visitas registradas
                            </p>
                          </div>
                          <div className="p-3 rounded-xl bg-sky-50 dark:bg-sky-900/20 border border-sky-100 dark:border-sky-800/30" data-id="element-3501">
                            <p className="text-2xl font-bold text-sky-700 dark:text-sky-400" data-id="element-3502">
                              {new Set(patientVisits.map(v => v.doctorName)).size}
                            </p>
                            <p className="text-xs text-sky-600 dark:text-sky-500 font-medium" data-id="element-3503">
                              Doctores atendieron
                            </p>
                          </div>
                          <div className="p-3 rounded-xl bg-purple-50 dark:bg-purple-900/20 border border-purple-100 dark:border-purple-800/30" data-id="element-3504">
                            <p className="text-sm font-bold text-purple-700 dark:text-purple-400 truncate" data-id="element-3505">
                              {patientVisits[0] ? formatDate(patientVisits[0].date) : '-'}
                            </p>
                            <p className="text-xs text-purple-600 dark:text-purple-500 font-medium" data-id="element-3506">
                              Última visita
                            </p>
                          </div>
                        </div>}

                      {patientVisits.length === 0 ? <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-dashed border-gray-200 dark:border-gray-700" data-id="element-3507">
                          <ClipboardListIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" data-id="element-3508" />
                          <p className="text-gray-500 dark:text-gray-400 font-medium" data-id="element-3509">
                            No hay registros de visitas
                          </p>
                          <p className="text-sm text-gray-400 dark:text-gray-500 mt-1" data-id="element-3510">
                            Los historiales clínicos creados para este paciente
                            aparecerán aquí
                          </p>
                        </div> : patientVisits.map((visit, index) => {
                  const isExpanded = expandedVisitId === visit.id;
                  return <div key={visit.id} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm" data-id="element-3511">
                              {/* Visit Header - Always visible */}
                              <button onClick={() => setExpandedVisitId(isExpanded ? null : visit.id)} className="w-full text-left p-4 flex items-center gap-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors" data-id="element-3512">
                                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white font-bold text-sm shadow-sm" data-id="element-3513">
                                  {index + 1}
                                </div>
                                <div className="flex-1 min-w-0" data-id="element-3514">
                                  <div className="flex items-center gap-2 mb-0.5" data-id="element-3515">
                                    <p className="font-semibold text-gray-900 dark:text-white text-sm" data-id="element-3516">
                                      {formatDate(visit.date)}
                                    </p>
                                    <span className="w-1 h-1 rounded-full bg-gray-300" data-id="element-3517"></span>
                                    <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-3518">
                                      Dr. {visit.doctorName}
                                    </p>
                                  </div>
                                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate" data-id="element-3519">
                                    {visit.personalBackground?.consultationReason ? `Motivo: ${visit.personalBackground.consultationReason}` : visit.diagnosis ? `Dx: ${visit.diagnosis.substring(0, 80)}${visit.diagnosis.length > 80 ? '...' : ''}` : 'Sin motivo registrado'}
                                  </p>
                                </div>
                                <div className="flex-shrink-0" data-id="element-3520">
                                  {isExpanded ? <ChevronUpIcon className="w-5 h-5 text-gray-400" data-id="element-3521" /> : <ChevronDownIcon className="w-5 h-5 text-gray-400" data-id="element-3522" />}
                                </div>
                              </button>

                              {/* Expanded Content */}
                              {isExpanded && <div className="border-t border-gray-100 dark:border-gray-700 p-4 space-y-4 bg-gray-50/50 dark:bg-gray-900/30" data-id="element-3523">
                                  {/* Signos Vitales de esta visita */}
                                  {visit.physicalExam && (visit.physicalExam.bloodPressure || visit.physicalExam.heartRate || visit.physicalExam.bloodSugar || visit.physicalExam.temperature || visit.physicalExam.weight || visit.physicalExam.height) && <div className="rounded-lg bg-teal-50 dark:bg-teal-900/20 border border-teal-100 dark:border-teal-800/30 p-3" data-id="element-3524">
                                        <div className="flex items-center gap-2 mb-2" data-id="element-3525">
                                          <ActivityIcon className="w-4 h-4 text-teal-600 dark:text-teal-400" data-id="element-3526" />
                                          <h4 className="text-xs font-semibold text-teal-700 dark:text-teal-300 uppercase tracking-wider" data-id="element-3527">
                                            Exploración Física
                                          </h4>
                                        </div>
                                        <div className="flex flex-wrap gap-3" data-id="element-3528">
                                          {visit.physicalExam.bloodPressure && <div className="flex items-center gap-1.5 bg-white dark:bg-gray-800 rounded-md px-2.5 py-1.5 border border-teal-100 dark:border-teal-800/30" data-id="element-3529">
                                              <span className="text-[10px] font-semibold text-gray-400 uppercase" data-id="element-3530">
                                                P.A.
                                              </span>
                                              <span className="text-sm font-bold text-gray-800 dark:text-gray-200" data-id="element-3531">
                                                {visit.physicalExam.bloodPressure}
                                              </span>
                                            </div>}
                                          {visit.physicalExam.heartRate && <div className="flex items-center gap-1.5 bg-white dark:bg-gray-800 rounded-md px-2.5 py-1.5 border border-teal-100 dark:border-teal-800/30" data-id="element-3532">
                                              <span className="text-[10px] font-semibold text-gray-400 uppercase" data-id="element-3533">
                                                F.C.
                                              </span>
                                              <span className="text-sm font-bold text-gray-800 dark:text-gray-200" data-id="element-3534">
                                                {visit.physicalExam.heartRate}{' '}
                                                bpm
                                              </span>
                                            </div>}
                                          {visit.physicalExam.bloodSugar && <div className="flex items-center gap-1.5 bg-white dark:bg-gray-800 rounded-md px-2.5 py-1.5 border border-teal-100 dark:border-teal-800/30" data-id="element-3535">
                                              <span className="text-[10px] font-semibold text-gray-400 uppercase" data-id="element-3536">
                                                Glucosa
                                              </span>
                                              <span className="text-sm font-bold text-gray-800 dark:text-gray-200" data-id="element-3537">
                                                {visit.physicalExam.bloodSugar}{' '}
                                                mg/dL
                                              </span>
                                            </div>}
                                          {visit.physicalExam.temperature && <div className="flex items-center gap-1.5 bg-white dark:bg-gray-800 rounded-md px-2.5 py-1.5 border border-teal-100 dark:border-teal-800/30" data-id="element-3538">
                                              <span className="text-[10px] font-semibold text-gray-400 uppercase" data-id="element-3539">
                                                Temp.
                                              </span>
                                              <span className="text-sm font-bold text-gray-800 dark:text-gray-200" data-id="element-3540">
                                                {visit.physicalExam.temperature}{' '}
                                                °C
                                              </span>
                                            </div>}
                                          {visit.physicalExam.weight && <div className="flex items-center gap-1.5 bg-white dark:bg-gray-800 rounded-md px-2.5 py-1.5 border border-teal-100 dark:border-teal-800/30" data-id="element-3541">
                                              <span className="text-[10px] font-semibold text-gray-400 uppercase" data-id="element-3542">
                                                Peso
                                              </span>
                                              <span className="text-sm font-bold text-gray-800 dark:text-gray-200" data-id="element-3543">
                                                {visit.physicalExam.weight} kg
                                              </span>
                                            </div>}
                                          {visit.physicalExam.height && <div className="flex items-center gap-1.5 bg-white dark:bg-gray-800 rounded-md px-2.5 py-1.5 border border-teal-100 dark:border-teal-800/30" data-id="element-3544">
                                              <span className="text-[10px] font-semibold text-gray-400 uppercase" data-id="element-3545">
                                                Altura
                                              </span>
                                              <span className="text-sm font-bold text-gray-800 dark:text-gray-200" data-id="element-3546">
                                                {visit.physicalExam.height} cm
                                              </span>
                                            </div>}
                                          {visit.physicalExam.lungs && <div className="flex items-center gap-1.5 bg-white dark:bg-gray-800 rounded-md px-2.5 py-1.5 border border-teal-100 dark:border-teal-800/30" data-id="element-3547">
                                              <span className="text-[10px] font-semibold text-gray-400 uppercase" data-id="element-3548">
                                                Pulmones
                                              </span>
                                              <span className="text-sm font-bold text-gray-800 dark:text-gray-200" data-id="element-3549">
                                                {visit.physicalExam.lungs}
                                              </span>
                                            </div>}
                                          {visit.physicalExam.abdomen && <div className="flex items-center gap-1.5 bg-white dark:bg-gray-800 rounded-md px-2.5 py-1.5 border border-teal-100 dark:border-teal-800/30" data-id="element-3550">
                                              <span className="text-[10px] font-semibold text-gray-400 uppercase" data-id="element-3551">
                                                Abdomen
                                              </span>
                                              <span className="text-sm font-bold text-gray-800 dark:text-gray-200" data-id="element-3552">
                                                {visit.physicalExam.abdomen}
                                              </span>
                                            </div>}
                                        </div>
                                      </div>}

                                  {/* Diagnóstico */}
                                  {visit.diagnosis && <div data-id="element-3553">
                                      <div className="flex items-center gap-2 mb-1.5" data-id="element-3554">
                                        <div className="w-6 h-6 rounded bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center" data-id="element-3555">
                                          <StethoscopeIcon className="w-3.5 h-3.5 text-rose-600 dark:text-rose-400" data-id="element-3556" />
                                        </div>
                                        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-3557">
                                          Diagnóstico
                                        </h4>
                                      </div>
                                      <p className="text-sm text-gray-700 dark:text-gray-300 pl-8 whitespace-pre-wrap" data-id="element-3558">
                                        {visit.diagnosis}
                                      </p>
                                    </div>}

                                  {/* Tratamiento */}
                                  {visit.treatment && <div data-id="element-3559">
                                      <div className="flex items-center gap-2 mb-1.5" data-id="element-3560">
                                        <div className="w-6 h-6 rounded bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center" data-id="element-3561">
                                          <PillIcon className="w-3.5 h-3.5 text-teal-600 dark:text-teal-400" data-id="element-3562" />
                                        </div>
                                        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-3563">
                                          Tratamiento
                                        </h4>
                                      </div>
                                      <p className="text-sm text-gray-700 dark:text-gray-300 pl-8 whitespace-pre-wrap" data-id="element-3564">
                                        {visit.treatment}
                                      </p>
                                    </div>}

                                  {/* Indicaciones Médicas */}
                                  {visit.medicalIndications && <div data-id="element-3565">
                                      <div className="flex items-center gap-2 mb-1.5" data-id="element-3566">
                                        <div className="w-6 h-6 rounded bg-sky-100 dark:bg-sky-900/30 flex items-center justify-center" data-id="element-3567">
                                          <FileTextIcon className="w-3.5 h-3.5 text-sky-600 dark:text-sky-400" data-id="element-3568" />
                                        </div>
                                        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-3569">
                                          Indicaciones Médicas
                                        </h4>
                                      </div>
                                      <p className="text-sm text-gray-700 dark:text-gray-300 pl-8 whitespace-pre-wrap" data-id="element-3570">
                                        {visit.medicalIndications}
                                      </p>
                                    </div>}

                                  {/* Sesiones */}
                                  {(visit.sessions || visit.sessionNumber) && <div data-id="element-3571">
                                      <div className="flex items-center gap-2 mb-1.5" data-id="element-3572">
                                        <div className="w-6 h-6 rounded bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center" data-id="element-3573">
                                          <CalendarIcon className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" data-id="element-3574" />
                                        </div>
                                        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-3575">
                                          Sesiones
                                        </h4>
                                      </div>
                                      <div className="pl-8 flex gap-4" data-id="element-3576">
                                        {visit.sessions && <p className="text-sm text-gray-700 dark:text-gray-300" data-id="element-3577">
                                            <span className="font-medium text-gray-500" data-id="element-3578">
                                              Sesiones:
                                            </span>{' '}
                                            {visit.sessions}
                                          </p>}
                                        {visit.sessionNumber && <p className="text-sm text-gray-700 dark:text-gray-300" data-id="element-3579">
                                            <span className="font-medium text-gray-500" data-id="element-3580">
                                              N° Sesión:
                                            </span>{' '}
                                            {visit.sessionNumber}
                                          </p>}
                                      </div>
                                    </div>}

                                  {/* Comentarios */}
                                  {visit.comments && <div data-id="element-3581">
                                      <div className="flex items-center gap-2 mb-1.5" data-id="element-3582">
                                        <div className="w-6 h-6 rounded bg-gray-200 dark:bg-gray-700 flex items-center justify-center" data-id="element-3583">
                                          <FileTextIcon className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" data-id="element-3584" />
                                        </div>
                                        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-3585">
                                          Comentarios
                                        </h4>
                                      </div>
                                      <p className="text-sm text-gray-700 dark:text-gray-300 pl-8 whitespace-pre-wrap" data-id="element-3586">
                                        {visit.comments}
                                      </p>
                                    </div>}

                                  {/* Firma */}
                                  {visit.doctorSignature && <div className="pt-3 border-t border-gray-200 dark:border-gray-700 pl-8" data-id="element-3587">
                                      <p className="text-xs text-gray-400" data-id="element-3588">
                                        Firma:{' '}
                                        <span className="font-medium text-gray-600 dark:text-gray-300" data-id="element-3589">
                                          {visit.doctorSignature}
                                        </span>
                                      </p>
                                    </div>}
                                </div>}
                            </div>;
                })}
                    </div>;
            })()}
              </TabPanel>
            </Tabs>
          </div>}
      </Modal>

      {/* Action Confirmation Modal */}
      <Modal isOpen={isActionConfirmOpen} onClose={() => setIsActionConfirmOpen(false)} title={actionType === 'archive' ? 'Archivar Paciente' : actionType === 'deactivate' ? 'Desactivar Paciente' : 'Eliminar Paciente'} size="sm" data-id="element-3590">
        <div className="space-y-4" data-id="element-3591">
          <p className="text-gray-600 dark:text-gray-400" data-id="element-3592">
            {actionType === 'archive' && `¿Estás seguro de que deseas archivar a "${selectedPatient?.name}"? Podrás restaurarlo más tarde.`}
            {actionType === 'deactivate' && `¿Estás seguro de que deseas desactivar a "${selectedPatient?.name}"? No aparecerá en las listas activas.`}
            {actionType === 'delete' && `¿Estás seguro de que deseas eliminar permanentemente a "${selectedPatient?.name}"? Esta acción no se puede deshacer.`}
          </p>
          <div className="flex justify-end gap-3" data-id="element-3593">
            <Button variant="outline" onClick={() => setIsActionConfirmOpen(false)} data-id="element-3594">
              Cancelar
            </Button>
            <Button variant={actionType === 'delete' ? 'danger' : 'primary'} onClick={handleAction} data-id="element-3595">
              {actionType === 'archive' ? 'Archivar' : actionType === 'deactivate' ? 'Desactivar' : 'Eliminar'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Excel Export Modal */}
      <ExcelExportModal isOpen={isExportModalOpen} onClose={() => setIsExportModalOpen(false)} onExport={handleExportToExcel} title="Exportar Pacientes" isExporting={isExporting} data-id="element-3596" />

      {/* PDF Export Modal */}
      <PdfExportModal isOpen={isPdfExportModalOpen} onClose={() => setIsPdfExportModalOpen(false)} onExport={handleExportToPdf} title="Exportar Pacientes a PDF" isExporting={isPdfExporting} data-id="element-3597" />
    </div>;
}